document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-interview-carousel]').forEach((root) => {
    const viewport = root.querySelector('.interview-carousel__viewport');
    const track = root.querySelector('.interview-carousel__track');
    const slides = Array.from(root.querySelectorAll('.interview-carousel__slide'));
    const dots = Array.from(root.querySelectorAll('.interview-carousel__dot'));
    const prev = root.querySelector('.interview-carousel__btn--prev');
    const next = root.querySelector('.interview-carousel__btn--next');

    if (!viewport || !track || slides.length === 0) return;

    const realCount = slides.length;

    const stepWidth = slides.length > 1
      ? Math.abs(slides[1].offsetLeft - slides[0].offsetLeft)
      : slides[0].offsetWidth;
    const visibleSlides = stepWidth > 0 ? Math.ceil(viewport.clientWidth / stepWidth) : 1;
    const CLONE_COUNT = Math.min(realCount, Math.max(2, visibleSlides + 1));

    slides.forEach((s, i) => {
      s.dataset.realIndex = String(i);
      s.dataset.clone = 'false';
    });

    const cloneSlide = (el) => {
      const c = el.cloneNode(true);
      c.dataset.realIndex = el.dataset.realIndex || '0';
      c.dataset.clone = 'true';
      c.setAttribute('aria-hidden', 'true');
      if (c.id) c.removeAttribute('id');
      return c;
    };

    const tailClones = slides.slice(-CLONE_COUNT).map(cloneSlide);
    tailClones.slice().reverse().forEach((c) => track.insertBefore(c, track.firstChild));
    const headClones = slides.slice(0, CLONE_COUNT).map(cloneSlide);
    headClones.forEach((c) => track.appendChild(c));

    const allSlides = Array.from(track.querySelectorAll('.interview-carousel__slide'));

    // After clones are prepended, scrollLeft=0 shows tail_clone not real_0.
    // Snap instantly to real slide 0 before first paint.
    const firstReal = track.querySelector('.interview-carousel__slide[data-real-index="0"][data-clone="false"]');
    if (firstReal) {
      viewport.scrollTo({
        left: Math.round(firstReal.offsetLeft - (viewport.clientWidth - firstReal.offsetWidth) / 2),
        behavior: 'auto',
      });
    }

    let activeIndex = 0;
    let clickTransitioning = false;
    let cloneJumping = false;
    let clickTimer = null;
    let cloneTimer = null;

    const syncDots = (idx) => {
      if (idx === -1 || idx === activeIndex) return;
      activeIndex = idx;
      dots.forEach((dot, i) => {
        const isActive = i === idx;
        dot.classList.toggle('is-active', isActive);
        if (isActive) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    };

    // Snap-aligned scrollLeft using offsetLeft (integer) to avoid float errors.
    const snapScrollLeftFor = (el) => {
      return Math.round(el.offsetLeft - (viewport.clientWidth - el.offsetWidth) / 2);
    };

    const scrollToEl = (el, behavior = 'smooth') => {
      clickTransitioning = true;
      if (clickTimer) clearTimeout(clickTimer);
      viewport.scrollTo({ left: snapScrollLeftFor(el), behavior });
      clickTimer = setTimeout(() => { clickTransitioning = false; }, behavior === 'smooth' ? 800 : 50);
    };

    // Wrap navigation using a PRE-JUMP instead of a post-scroll clone jump.
    //
    // Strategy: instantly jump to a clone of the CURRENT slide (same visual content,
    // no perceived change), then smooth-scroll one step to the real target.
    // This completely avoids restoring scroll-snap after a clone jump, which was
    // the root cause of the visible jerk.
    //
    // next wrap (last → first): pre-jump from real_N to tail_clone_N (left side),
    //   then scroll RIGHT one step to real_0.
    // prev wrap (first → last): pre-jump from real_0 to head_clone_0 (right side),
    //   then scroll LEFT one step to real_N.
    const navigateWrap = (currentIdx, targetIdx) => {
      const currentReal = track.querySelector(
        `.interview-carousel__slide[data-real-index="${currentIdx}"][data-clone="false"]`
      );
      const realTarget = track.querySelector(
        `.interview-carousel__slide[data-real-index="${targetIdx}"][data-clone="false"]`
      );
      if (!currentReal || !realTarget) return;

      // For next-wrap: targetIdx < currentIdx → need tail clone (left of real slides).
      // For prev-wrap: targetIdx > currentIdx → need head clone (right of real slides).
      const wantRight = targetIdx > currentIdx;
      const cloneOfCurrent = Array.from(
        track.querySelectorAll(
          `.interview-carousel__slide[data-real-index="${currentIdx}"][data-clone="true"]`
        )
      ).find(c => wantRight ? c.offsetLeft > currentReal.offsetLeft : c.offsetLeft < currentReal.offsetLeft);

      if (!cloneOfCurrent) {
        // Fallback: no suitable clone found — just scroll directly.
        syncDots(targetIdx);
        scrollToEl(realTarget);
        return;
      }

      syncDots(targetIdx);
      clickTransitioning = true;

      // Instant pre-jump: snap disabled, move to clone position (same visual).
      // Use scrollTo({ behavior: 'auto' }) instead of direct scrollLeft assignment —
      // direct assignment is silently ignored on iOS Safari with scroll-snap active.
      const delta = cloneOfCurrent.offsetLeft - currentReal.offsetLeft;
      viewport.style.scrollSnapType = 'none';
      viewport.scrollTo({ left: viewport.scrollLeft + delta, behavior: 'auto' });

      requestAnimationFrame(() => {
        viewport.scrollTo({ left: snapScrollLeftFor(realTarget), behavior: 'smooth' });
        if (clickTimer) clearTimeout(clickTimer);
        clickTimer = setTimeout(() => {
          viewport.style.scrollSnapType = '';
          clickTransitioning = false;
        }, 800);
      });
    };

    // Clone jump for MANUAL SWIPE only (button nav never lands on clones anymore).
    const jumpFromCloneToReal = (cloneEl, realEl) => {
      const delta = Math.round(cloneEl.offsetLeft - realEl.offsetLeft);
      viewport.style.scrollSnapType = 'none';
      viewport.scrollTo({ left: viewport.scrollLeft - delta, behavior: 'auto' });
      setTimeout(() => {
        viewport.style.scrollSnapType = '';
        if (cloneTimer) clearTimeout(cloneTimer);
        cloneTimer = setTimeout(() => { cloneJumping = false; }, 50);
      }, 50);
    };

    const getCenteredSlide = () => {
      const vRect = viewport.getBoundingClientRect();
      const center = vRect.left + vRect.width / 2;
      let best = null;
      let minDist = Infinity;
      allSlides.forEach((s) => {
        const r = s.getBoundingClientRect();
        const dist = Math.abs(r.left + r.width / 2 - center);
        if (dist < minDist) { minDist = dist; best = s; }
      });
      return best;
    };

    const syncOnScroll = () => {
      if (cloneJumping) return;

      const centered = getCenteredSlide();
      if (!centered) return;

      const realIdx = Number(centered.dataset.realIndex || '0');
      if (!clickTransitioning) syncDots(realIdx);

      // Handle manual swipe into clone zone.
      if (centered.dataset.clone === 'true') {
        const realTarget = track.querySelector(
          `.interview-carousel__slide[data-real-index="${realIdx}"][data-clone="false"]`
        );
        if (realTarget) {
          cloneJumping = true;
          requestAnimationFrame(() => jumpFromCloneToReal(centered, realTarget));
        }
      }
    };

    let scrollTimer;
    viewport.addEventListener('scroll', () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(syncOnScroll, 100);
    }, { passive: true });

    prev?.addEventListener('click', (e) => {
      e.preventDefault();
      if (clickTransitioning || cloneJumping) return;
      const targetIdx = (activeIndex - 1 + realCount) % realCount;
      if (targetIdx > activeIndex) {
        // Wrap: first → last
        navigateWrap(activeIndex, targetIdx);
      } else {
        syncDots(targetIdx);
        scrollToEl(track.querySelector(
          `.interview-carousel__slide[data-real-index="${targetIdx}"][data-clone="false"]`
        ));
      }
    });

    next?.addEventListener('click', (e) => {
      e.preventDefault();
      if (clickTransitioning || cloneJumping) return;
      const targetIdx = (activeIndex + 1) % realCount;
      if (targetIdx < activeIndex) {
        // Wrap: last → first
        navigateWrap(activeIndex, targetIdx);
      } else {
        syncDots(targetIdx);
        scrollToEl(track.querySelector(
          `.interview-carousel__slide[data-real-index="${targetIdx}"][data-clone="false"]`
        ));
      }
    });

    dots.forEach((dot, i) => {
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        if (clickTransitioning || cloneJumping) return;
        const target = track.querySelector(
          `.interview-carousel__slide[data-real-index="${i}"][data-clone="false"]`
        );
        if (target) { syncDots(i); scrollToEl(target); }
      });
    });
  });
});
